/**
 * What the phone can see, shown on the laptop while it is being placed.
 *
 * Placement was the one setup step with no feedback. The diagram next to this
 * shows what a good placement LOOKS like; this shows whether you achieved it.
 * Everything else in setup is verifiable on the laptop screen — the webcam
 * preview, the screen share, the pairing tick — and the phone, the one device
 * pointing away from the student, was the one they had to guess at.
 *
 * **The hard part is not showing the picture, it is refusing to show a stale
 * one.** The frame is stored on the server, not streamed: when the phone stops
 * publishing, the server goes on serving the last frame forever. So "the
 * request succeeded" says nothing about whether the picture is current, and a
 * panel that trusts a 200 stays confidently green while the student aligns
 * their phone against a photograph of where it used to point — walking into a
 * three-hour exam aimed at the ceiling, which is the exact outcome this panel
 * exists to prevent.
 *
 * Freshness therefore comes from the FRAME, never from the response:
 *
 *  - the age the server stamped on it, and
 *  - whether the ETag has moved, which needs no cooperation from CORS and so
 *    survives a split-origin deploy where a custom header may not.
 *
 * It must also never block the step. A phone on a network too poor to relay
 * stills can still record perfectly well, so a student who never sees a
 * picture can still confirm placement and sit the exam.
 */
import { useEffect, useRef, useState } from "react";
import { Smartphone, TriangleAlert } from "lucide-react";
import { clearViewfinder, fetchViewfinderFrame } from "@/lib/exam-api";
import { viewfinderFreshness } from "@/lib/phone/viewfinderFreshness";
import { cn } from "@/lib/cn";

/**
 * Slightly faster than the phone publishes (700 ms), so the laptop is never
 * the reason a frame is late. Unchanged frames cost a 304, not a download.
 */
const POLL_MS = 600;

interface Shown {
  url: string;
  /** Local time the bytes last CHANGED, not the last successful request. */
  changedAt: number;
  etag: string | null;
}

export function PhoneViewfinder({ attemptId }: { attemptId: string }) {
  const [shown, setShown] = useState<Shown | null>(null);
  /** Server-reported age of the newest frame, when the header is readable. */
  const [serverAgeMs, setServerAgeMs] = useState<number | null>(null);
  /** Re-renders on a timer so the picture can go stale while nothing arrives. */
  const [, setTick] = useState(0);

  // Mirrors `shown` for the polling loop, which must not restart on each new
  // frame — restarting the effect every 700 ms would tear down and rebuild the
  // interval and the abort controller continuously.
  const current = useRef<Shown | null>(null);

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();

    const tick = async () => {
      try {
        const res = await fetchViewfinderFrame(attemptId, {
          etag: current.current?.etag,
          signal: controller.signal,
        });

        if (res.kind === "frame" && cancelled) {
          // The object URL is created inside the fetch helper, so an abort
          // that lands after the body was read would otherwise pin the blob
          // for the lifetime of the document.
          URL.revokeObjectURL(res.frame.url);
          return;
        }
        if (cancelled) return;

        if (res.kind === "unchanged") {
          // A successful request carrying no new picture. Deliberately does
          // NOT touch `changedAt`: this is the single most important line in
          // the file, because treating it as freshness is what would make the
          // panel permanently green.
          return;
        }

        if (res.kind === "none") return;

        const previous = current.current;
        const next: Shown = {
          url: res.frame.url,
          changedAt: Date.now(),
          etag: res.frame.etag,
        };
        current.current = next;
        setShown(next);
        setServerAgeMs(res.frame.ageMs);
        // Revoked only after its replacement is handed to React, so the <img>
        // never flashes blank between frames.
        if (previous) URL.revokeObjectURL(previous.url);
      } catch {
        // Ignored: a failed poll is not itself evidence of anything, and the
        // staleness clock below is what reports a phone that has gone quiet.
      }
    };

    void tick();
    const poll = setInterval(() => void tick(), POLL_MS);
    // Independent of the poll, so the picture goes stale even if every request
    // is failing or hanging.
    const age = setInterval(() => setTick((n) => n + 1), 1000);

    return () => {
      cancelled = true;
      controller.abort();
      clearInterval(poll);
      clearInterval(age);
      if (current.current) URL.revokeObjectURL(current.current.url);
      current.current = null;

      // The frame is a picture of the student's room. Confirming placement
      // deletes it, but that is only the happy path — a student who abandons
      // setup, closes the tab or goes back to the exam list would otherwise
      // leave it in the bucket, where nothing else knows it exists: it is
      // deliberately never written to `attempt_media`, so no retention, purge
      // or reset path can see it. Deleting whenever this panel goes away
      // covers every exit.
      void clearViewfinder(attemptId).catch(() => {
        // Best effort. The server also refuses new frames once placement is
        // past, so the worst case is one stale object rather than a growing
        // relay.
      });
    };
  }, [attemptId]);

  // The rule itself lives in `viewfinderFreshness`, where it is unit-tested:
  // it is the only part of this panel that can be wrong without anyone
  // noticing, and it was, the first time.
  const { waiting, stale } = viewfinderFreshness({
    changedAtMs: shown?.changedAt ?? null,
    serverAgeMs,
    nowMs: Date.now(),
  });

  return (
    <figure className="m-0">
      <div
        className={cn(
          "relative aspect-[4/3] w-full overflow-hidden rounded-lg border bg-ink/5",
          stale ? "border-warn" : "border-border",
        )}
      >
        {waiting || shown === null ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 px-4 text-center">
            <Smartphone className="size-6 text-ink-faint" aria-hidden />
            <p className="text-xs text-ink-muted">
              Waiting for the phone’s picture…
            </p>
            <p className="text-2xs text-ink-faint">
              This can take a few seconds. You do not need to touch the phone.
            </p>
          </div>
        ) : (
          <img
            src={shown.url}
            alt={
              stale
                ? "Last picture received from your phone's camera — it has stopped updating"
                : "Live view from your phone's camera"
            }
            className={cn(
              "size-full object-cover transition-opacity",
              stale && "opacity-40",
            )}
          />
        )}

        {/* Announced, because a screen-reader user has no other way to learn
            that the picture stopped being current. */}
        <div role="status" aria-live="polite">
          {stale ? (
            <div className="absolute inset-x-0 bottom-0 flex items-center gap-1.5 bg-warn-soft/95 px-2.5 py-1.5">
              <TriangleAlert
                className="size-3.5 shrink-0 text-warn"
                aria-hidden
              />
              <p className="text-2xs font-medium text-warn">
                This picture has stopped updating — it may not be what the phone
                sees now. Check the phone is awake and still connected.
              </p>
            </div>
          ) : null}
        </div>
      </div>

      <figcaption className="mt-1.5 text-2xs text-ink-faint">
        {stale
          ? "Last picture received."
          : "Updates about once a second. Move the phone until you and your desk are both in view."}
      </figcaption>
    </figure>
  );
}
